import { Injectable } from '@angular/core';

import { map, Observable, of } from 'rxjs';

import { TimeZone } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { observableInSeparateThread } from '../helper/observable-helper';

/**
 * Service to get TimeZones
 */
@Injectable()
export class TimeZoneService {
    private cachedTimeZoneMap: { [apiRequest: string]: TimeZone[]; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all TimeZones
     */
    public getIndex(queryParams: string[] = [], forceRefresh: boolean = false): Observable<TimeZone[]> {
        let apiUrl: string = `${this.http.configService.getApiBaseUrl()}/time-zones/`;

        if (queryParams) {
            apiUrl += this.http.getQueryParameters(null, queryParams);
        }

        if (!forceRefresh &&
            this.cachedTimeZoneMap[apiUrl]) {
            // if `cachedTimeZones` is available just return it as `Observable`
            return observableInSeparateThread(this.cachedTimeZoneMap[apiUrl]);
        }
        // Make the http request
        return this.http.get(apiUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const timeZones: TimeZone[] = [];
                const responseBody: any = response;
                for (const timeZone of responseBody) {
                    timeZones.push((new TimeZone()).deserialize(timeZone));
                }
                this.cachedTimeZoneMap[apiUrl] = timeZones;
                return timeZones;
            }));
    }

    /**
     * Get the time zone identifier from a given location
     */
    public getTimeZoneIdFromLocation(latitude: number, longitude: number): Observable<TimeZone[]> {
        const queryParams: string[] =
            [
                `latitude=${latitude}`,
                `longitude=${longitude}`,
            ];
        return this.getIndex(queryParams);
    }

    public getTimeZoneFromGooglePlace(googlePlace: any): Observable<TimeZone> {
        if (googlePlace &&
            googlePlace.geometry &&
            googlePlace.geometry.location) {
            const latitude: number = googlePlace.geometry.location.lat();
            const longitude: number = googlePlace.geometry.location.lng();

            return this.getTimeZoneIdFromLocation(latitude, longitude)
                .pipe(map((timeZones: TimeZone[]) => (timeZones && timeZones.length ? timeZones[0] : null)));
        }
    }
}
